<script setup lang="ts">
import { AddIcon, DismissIcon } from '@blpde/nx-icons';
import { computed, inject, nextTick, ref, watch } from 'vue';
import { NxButton } from '../Button';
import { NxCheckbox, NxTextInput } from '../Inputs';
import { i18nInjectionKey } from '../internal';
import { NxPopover } from '../Popover';
import { NxToggleGroup } from '../ToggleGroup';
import NxTableFilterConditionInputs from './NxTableFilterConditionInputs.vue';
import {
  allowsFilterOperator,
  cloneColumnFilter,
  isConditionComplete,
  normalizeColumnFilter,
  restrictedFilterOperators,
  type FilterModifier,
  type NxTableColumnFilter,
  type NxTableFilterCondition,
  type NxTableFilterKind,
  type NxTableFilterOperator,
} from './filterModel';

/**
 * Funnel button in a column header + the filter popover behind it.
 *
 * Productivity-first: the popover opens in a SIMPLE mode.
 * - string / select / bool columns: an Excel-style searchable value checklist
 *   (string values come from `valueSource` — by default the distinct values of
 *   the loaded rows, optionally a GraphQL-backed resolver). One click filters
 *   instantly; on string columns the search term itself filters the table live
 *   as a "contains" filter while typing (Enter closes).
 * - number / currency / date / time columns: one focused condition that
 *   applies live while typing (Enter closes).
 * The full operator/multi-condition editor sits behind "Erweitert".
 * Emits the normalized column filter (`null` = clear).
 */
const props = withDefaults(
  defineProps<{
    columnId: string;
    displayName: string;
    kind: NxTableFilterKind;
    options?: string[];
    /** Display label for a select option value (e.g. enum code → label). */
    optionLabel?: (value: string) => string;
    /** Checklist values for a search term (string kind; may be async). */
    valueSource?: (search: string) => string[] | Promise<string[]>;
    /** Show the search box in the checklist (default true; bool never has one). */
    searchable?: boolean;
    /** Operators offered (a column's `filterOperators`); all of the kind by default. */
    operators?: readonly NxTableFilterOperator[];
    /** How conditions may combine (a column's `filterModifiers`); and + or by default. */
    modifiers?: readonly FilterModifier[];
    disabled?: boolean;
    filter: NxTableColumnFilter | undefined;
    /** Active legacy quick-filter values of the column (filter row / bridge). */
    quickValues?: readonly unknown[];
  }>(),
  {
    options: () => [],
    optionLabel: undefined,
    valueSource: undefined,
    searchable: true,
    operators: undefined,
    modifiers: undefined,
    disabled: false,
    quickValues: () => [],
  }
);

const emit = defineEmits<{ apply: [filter: NxTableColumnFilter | null] }>();

const i18n = inject(i18nInjectionKey)!;

const open = ref(false);
const panelRef = ref<HTMLElement | null>(null);

const cleanQuickValues = computed(() =>
  (props.quickValues ?? []).filter((v) => v !== null && v !== undefined && v !== '').map(String)
);

/** Badge: structured conditions + the column's quick filter (bridged or typed). */
const activeCount = computed(
  () =>
    (normalizeColumnFilter(props.filter)?.conditions.length ?? 0) +
    (cleanQuickValues.value.length > 0 ? 1 : 0)
);

const allows = (operator: NxTableFilterOperator) =>
  allowsFilterOperator(props.kind, props.operators, operator);

/**
 * Kinds filtered through the one-click value checklist — a column that
 * restricts its operators keeps it only when it allows `oneOf` (a bool column
 * then falls back to its restricted editor: yes / no).
 */
const isChecklistKind = computed(
  () =>
    (props.kind === 'select' || props.kind === 'bool' || props.kind === 'string') && allows('oneOf')
);
/** The "exclude" switch of the checklist needs `noneOf`. */
const canExclude = computed(() => props.kind !== 'bool' && allows('noneOf'));
/** The live search of a text column's checklist is a "contains" filter. */
const canSearchContains = computed(() => allows('contains'));
/** The operator a bridged quick value reads as: a text column's is "contains". */
const quickOperator = computed<NxTableFilterOperator>(() =>
  props.kind === 'string' ? 'contains' : 'equals'
);
const operatorChoices = computed(() => restrictedFilterOperators(props.kind, props.operators));
/** A checklist-only column (`filterOperators: ['oneOf']`) has no condition editor to open. */
const canAdvanced = computed(() => operatorChoices.value.length > 0);
const modifierChoices = computed<FilterModifier[]>(() =>
  props.modifiers && props.modifiers.length > 0 ? [...props.modifiers] : ['and', 'or']
);
const showSearch = computed(() => props.searchable && props.kind !== 'bool');

function valueLabel(value: string): string {
  if (props.kind === 'bool') {
    return value === 'true'
      ? i18n.t('common.table.filter.bool_true')
      : i18n.t('common.table.filter.bool_false');
  }
  return props.optionLabel?.(value) ?? value;
}

// ---- mode ----
const mode = ref<'simple' | 'advanced'>('simple');

// ---- checklist state (string / select / bool) ----
const selectedValues = ref<string[]>([]);
const excludeValues = ref(false);
const searchTerm = ref('');
const loadedValues = ref<string[]>([]);
const valuesLoading = ref(false);
let loadSeq = 0;
let searchTimer: ReturnType<typeof setTimeout> | undefined;
/** Opening the popover syncs `searchTerm` from the active filter — that sync
 *  must not re-emit (or clear) the filter as if the user had typed. */
let skipNextLiveApply = false;

async function loadValues() {
  const search = searchTerm.value.trim();
  if (props.kind === 'bool') {
    loadedValues.value = ['true', 'false'];
    return;
  }
  if (props.kind === 'select' && !props.valueSource) {
    const query = search.toLocaleLowerCase();
    loadedValues.value = query
      ? props.options.filter((v) => valueLabel(v).toLocaleLowerCase().includes(query))
      : props.options;
    return;
  }
  const seq = ++loadSeq;
  const source = props.valueSource?.(search) ?? [];
  if (Array.isArray(source)) {
    loadedValues.value = source;
    return;
  }
  valuesLoading.value = true;
  try {
    const values = await source;
    if (seq === loadSeq) loadedValues.value = values;
  } finally {
    if (seq === loadSeq) valuesLoading.value = false;
  }
}

watch(searchTerm, () => {
  if (!open.value) return;
  // Consume the sync flag PER CHANGE (here, not in the timer): only the
  // programmatic sync itself is exempt — the user's very next edit must
  // live-apply, or a cleared term leaves the stale filter active.
  const isSync = skipNextLiveApply;
  skipNextLiveApply = false;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    loadValues();
    if (!isSync) applyLiveSearch();
  }, 150);
});

/** String columns: the search term filters the table live while typing
 *  (contains), as long as no checklist values are checked. */
function applyLiveSearch() {
  if (props.kind !== 'string' || selectedValues.value.length > 0) return;
  if (!canSearchContains.value) return;
  const term = searchTerm.value.trim();
  emit(
    'apply',
    term ? { modifier: 'and', conditions: [{ operator: 'contains', value: term }] } : null
  );
}

/** Checked values stay visible (pinned first) even when the search hides them. */
const displayedValues = computed(() => {
  const loaded = new Set(loadedValues.value);
  const pinned = selectedValues.value.filter((v) => !loaded.has(v));
  return [...pinned, ...loadedValues.value];
});

// ---- simple state: one condition (number / currency / date / time) ----
function emptyCondition(): NxTableFilterCondition {
  return { operator: operatorChoices.value[0] };
}
const simpleCondition = ref<NxTableFilterCondition>(emptyCondition());

// ---- advanced state ----
const draft = ref<{ modifier: FilterModifier; conditions: NxTableFilterCondition[] }>({
  modifier: 'and',
  conditions: [emptyCondition()],
});

/** Sync `searchTerm` from props without triggering the live contains-apply. */
function syncSearchTerm(value: string) {
  if (searchTerm.value === value) return;
  skipNextLiveApply = true;
  searchTerm.value = value;
}

watch(open, (isOpen) => {
  if (!isOpen) {
    clearTimeout(searchTimer);
    skipNextLiveApply = false;
    return;
  }
  const normalized = normalizeColumnFilter(props.filter);
  const single = normalized?.conditions.length === 1 ? normalized.conditions[0] : undefined;
  const quick = cleanQuickValues.value;
  if (isChecklistKind.value) {
    const isChecklist = single && (single.operator === 'oneOf' || single.operator === 'noneOf');
    const isContains = props.kind === 'string' && single?.operator === 'contains';
    if (!normalized || isChecklist || isContains) {
      mode.value = 'simple';
      if (isChecklist) {
        // A stored exclusion on a column that cannot exclude (any more) is dropped
        // explicitly — the cleared filter is emitted — instead of staying active
        // behind an empty checklist and turning into "only these" on the next pick.
        const lostExclusion = single!.operator === 'noneOf' && !canExclude.value;
        selectedValues.value = lostExclusion ? [] : [...(single!.values ?? [])];
        if (lostExclusion) emit('apply', null);
        syncSearchTerm('');
      } else if (isContains) {
        selectedValues.value = [];
        syncSearchTerm(single!.value ?? '');
      } else if (props.kind === 'string' && quick.length === 1) {
        // Bridged quick value on a text column: a single term reads as the
        // active contains-search.
        selectedValues.value = [];
        syncSearchTerm(quick[0]);
      } else {
        // Bridged quick values (checklist picks / select filter row).
        selectedValues.value = quick;
        syncSearchTerm('');
      }
      excludeValues.value = single?.operator === 'noneOf' && canExclude.value;
      loadValues();
    } else if (canAdvanced.value) {
      enterAdvanced(normalized);
    } else {
      // A stored condition a checklist-only column cannot edit: the checklist opens
      // empty; the next pick replaces the stored filter, "Leeren" removes it.
      mode.value = 'simple';
      selectedValues.value = [];
      excludeValues.value = false;
      syncSearchTerm('');
      loadValues();
    }
  } else if (!normalized || normalized.conditions.length === 1) {
    mode.value = 'simple';
    simpleCondition.value = single
      ? { ...single }
      : quick.length > 0 && allows(quickOperator.value)
        ? { operator: quickOperator.value, value: quick[0] }
        : emptyCondition();
  } else {
    enterAdvanced(normalized);
  }
  // Fast path: caret straight into the first input.
  nextTick(() => panelRef.value?.querySelector<HTMLElement>('input')?.focus());
});

function enterAdvanced(base?: NxTableColumnFilter | null) {
  const source =
    base ??
    (isChecklistKind.value && selectedValues.value.length > 0
      ? checklistFilter()
      : !isChecklistKind.value && isConditionComplete(simpleCondition.value)
        ? { modifier: 'and' as const, conditions: [simpleCondition.value] }
        : normalizeColumnFilter(props.filter));
  draft.value = source
    ? cloneColumnFilter(source)
    : { modifier: 'and', conditions: [emptyCondition()] };
  // A stored "or" of a column that does not offer it reads as the first modifier it does.
  if (!modifierChoices.value.includes(draft.value.modifier)) {
    draft.value.modifier = modifierChoices.value[0];
  }
  if (draft.value.conditions.length === 0) draft.value.conditions.push(emptyCondition());
  mode.value = 'advanced';
}

// ---- checklist: instant apply ----
function checklistFilter(): NxTableColumnFilter | null {
  if (selectedValues.value.length === 0) return null;
  return {
    modifier: 'and',
    conditions: [
      {
        operator: excludeValues.value && canExclude.value ? 'noneOf' : 'oneOf',
        values: [...selectedValues.value],
      },
    ],
  };
}

function toggleValue(value: string) {
  selectedValues.value = selectedValues.value.includes(value)
    ? selectedValues.value.filter((v) => v !== value)
    : [...selectedValues.value, value];
  emit('apply', checklistFilter());
}

function toggleExclude() {
  excludeValues.value = !excludeValues.value;
  if (selectedValues.value.length > 0) emit('apply', checklistFilter());
}

/** Enter in the search box: apply the live "contains" filter now and close. */
function onSearchEnter() {
  // Without "contains" the box only narrows the value list: Enter filters nothing.
  if (!canSearchContains.value) return;
  clearTimeout(searchTimer);
  applyLiveSearch();
  open.value = false;
}

function onSimpleConditionUpdate(next: NxTableFilterCondition) {
  simpleCondition.value = next;
  // Live apply, like the quick filter row: complete → filter, incomplete → clear.
  emit('apply', isConditionComplete(next) ? { modifier: 'and', conditions: [{ ...next }] } : null);
}

function clearSimple() {
  selectedValues.value = [];
  excludeValues.value = false;
  syncSearchTerm('');
  simpleCondition.value = emptyCondition();
  emit('apply', null);
  if (isChecklistKind.value) loadValues();
}

// ---- advanced ----
const canApply = computed(() => draft.value.conditions.some(isConditionComplete));

const modifierOptions = computed(() =>
  modifierChoices.value.map((value) => ({
    value,
    label: i18n.t(value === 'and' ? 'common.table.filter.and' : 'common.table.filter.or'),
  }))
);

function addCondition() {
  draft.value.conditions.push(emptyCondition());
}

function removeCondition(index: number) {
  draft.value.conditions.splice(index, 1);
  if (draft.value.conditions.length === 0) draft.value.conditions.push(emptyCondition());
}

function applyAdvanced() {
  emit('apply', normalizeColumnFilter(draft.value) ?? null);
  open.value = false;
}

function clearAdvanced() {
  emit('apply', null);
  open.value = false;
}
</script>

<template>
  <!-- stop: header click sorts, drag reorders — the funnel must do neither -->
  <span class="header-filter" @click.stop @mousedown.stop @dragstart.stop.prevent>
    <!-- lazy: one popover per column header — hidden panels must not keep
         floating-ui autoUpdate scroll listeners alive (table scroll perf).
         bottom-start: panel hangs left-aligned under the funnel so it reads
         as attached to its column (no arrow — it clips the corner radius). -->
    <NxPopover v-model:open="open" side="bottom-start" lazy>
      <template #trigger>
        <button
          type="button"
          :class="['filter-trigger', { active: activeCount > 0, open }]"
          :disabled="props.disabled"
          :title="i18n.t('common.table.filter.title', { column: props.displayName })"
          :aria-label="i18n.t('common.table.filter.title', { column: props.displayName })"
          :aria-expanded="open"
          :aria-pressed="activeCount > 0"
        >
          <svg class="filter-glyph" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M3.25 4.25h13.5l-5.2 6v4.15l-3.1 1.6v-5.75l-5.2-6Z" />
          </svg>
          <span v-if="activeCount > 0" class="filter-count">{{ activeCount }}</span>
        </button>
      </template>

      <div
        ref="panelRef"
        :class="[
          'filter-panel',
          mode === 'simple' && isChecklistKind ? 'mode-list' : 'mode-condition',
        ]"
      >
        <div class="filter-panel-header">
          <span class="filter-panel-title">
            {{ i18n.t('common.table.filter.title', { column: props.displayName }) }}
          </span>
          <span v-if="activeCount > 0" class="filter-panel-status">{{ activeCount }}</span>
        </div>

        <!-- ── simple: searchable one-click value checklist ──────────────── -->
        <template v-if="mode === 'simple' && isChecklistKind">
          <NxTextInput
            v-if="showSearch"
            v-model="searchTerm"
            height="xs"
            :placeholder="
              props.kind === 'string'
                ? i18n.t('common.table.filter.search_hint_string')
                : i18n.t('common.table.filter.search_values')
            "
            @keydown.enter.prevent="onSearchEnter"
          />
          <div class="check-list">
            <label v-for="option in displayedValues" :key="option" class="check-row">
              <NxCheckbox
                :model-value="selectedValues.includes(option)"
                @update:model-value="() => toggleValue(option)"
              />
              <span class="check-label">{{ valueLabel(option) }}</span>
            </label>
            <div v-if="valuesLoading" class="check-note">…</div>
            <div v-else-if="displayedValues.length === 0" class="check-note">
              {{ i18n.t('common.table.filter.no_values') }}
            </div>
          </div>
          <label v-if="canExclude" class="check-row exclude-row">
            <NxCheckbox :model-value="excludeValues" @update:model-value="toggleExclude" />
            <span class="check-label">{{ i18n.t('common.table.filter.exclude') }}</span>
          </label>
          <div class="simple-footer">
            <button type="button" class="text-action" @click="clearSimple">
              {{ i18n.t('common.table.filter.clear') }}
            </button>
            <span class="footer-spacer"></span>
            <button
              v-if="canAdvanced"
              type="button"
              class="text-action muted"
              @click="enterAdvanced()"
            >
              {{ i18n.t('common.table.filter.advanced') }}
            </button>
          </div>
        </template>

        <!-- ── simple: one live condition (number / currency / date / …) ─── -->
        <template v-else-if="mode === 'simple'">
          <div class="condition-row" @keydown.enter.prevent="open = false">
            <NxTableFilterConditionInputs
              :model-value="simpleCondition"
              :kind="props.kind"
              :options="props.options"
              :option-label="props.optionLabel"
              :allowed-operators="props.operators"
              @update:model-value="onSimpleConditionUpdate"
            />
          </div>
          <div class="simple-footer">
            <button type="button" class="text-action" @click="clearSimple">
              {{ i18n.t('common.table.filter.clear') }}
            </button>
            <span class="footer-spacer"></span>
            <button
              v-if="canAdvanced"
              type="button"
              class="text-action muted"
              @click="enterAdvanced()"
            >
              {{ i18n.t('common.table.filter.advanced') }}
            </button>
          </div>
        </template>

        <!-- ── advanced: full multi-condition editor ─────────────────────── -->
        <template v-else>
          <div class="panel-head" @keydown.enter.prevent="canApply && applyAdvanced()">
            <span class="panel-title">
              {{ i18n.t('common.table.filter.title', { column: props.displayName }) }}
            </span>
            <NxToggleGroup
              v-if="draft.conditions.length > 1 && modifierChoices.length > 1"
              v-model="draft.modifier"
              :options="modifierOptions"
              height="xs"
            />
          </div>

          <div
            v-for="(condition, index) in draft.conditions"
            :key="index"
            class="condition-row"
            @keydown.enter.prevent="canApply && applyAdvanced()"
          >
            <NxTableFilterConditionInputs
              :model-value="condition"
              :kind="props.kind"
              :options="props.options"
              :option-label="props.optionLabel"
              :allowed-operators="props.operators"
              @update:model-value="(next) => (draft.conditions[index] = next)"
            />
            <button
              type="button"
              class="remove-condition"
              :title="i18n.t('common.table.filter.remove_condition')"
              @click="removeCondition(index)"
            >
              <DismissIcon />
            </button>
          </div>

          <button type="button" class="add-condition" @click="addCondition">
            <AddIcon />
            {{ i18n.t('common.table.filter.add_condition') }}
          </button>

          <div class="panel-actions">
            <NxButton height="s" :disabled="!canApply && activeCount === 0" @click="applyAdvanced">
              {{ i18n.t('common.table.filter.apply') }}
            </NxButton>
            <NxButton variant="outline" height="s" @click="clearAdvanced">
              {{ i18n.t('common.table.filter.clear') }}
            </NxButton>
          </div>
        </template>
      </div>
    </NxPopover>
  </span>
</template>

<style scoped>
.header-filter {
  display: inline-flex;
  align-items: center;
  /* The space scale starts at 2xs — `--nxt-space-3xs` resolves to nothing and the
     declaration is dropped altogether (I-771). */
  margin-left: var(--nxt-space-2xs);
}

.filter-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--nxt-control-size-xs);
  height: var(--nxt-control-size-xs);
  padding: 0;
  border: var(--nxt-hairline) solid transparent;
  border-radius: var(--nxt-radius-md);
  background: transparent;
  /* At rest the table's header icon colour (light on a brand-filled header). */
  color: var(--nxt-table--header-icon, var(--nxt-fg-subtle));
  cursor: pointer;
  transition:
    color 0.12s ease,
    background-color 0.12s ease,
    border-color 0.12s ease,
    box-shadow 0.12s ease;

  &:hover {
    border-color: var(--nxt-border);
    background-color: var(--nxt-surface-sunken-bg);
    color: var(--nxt-fg);
  }
  &.active,
  &.open {
    border-color: var(--nxt-brand-border, var(--nxt-brand-bg));
    background: var(--nxt-brand-subtle-bg);
    color: var(--nxt-brand-bg);
  }
  &.open {
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring);
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  /* The table's focus colour: the kit's ring, the colour on the fill under a
     brand-filled header (I-2510). */
  &:focus-visible {
    outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-table--focus, var(--nxt-focus-ring));
    outline-offset: calc(var(--nxt-size-unit) * 0.0625);
  }
}

.filter-glyph {
  width: var(--nxt-icon-size-md);
  height: var(--nxt-icon-size-md);
  overflow: visible;
}
.filter-glyph path {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.filter-trigger.active .filter-glyph path {
  fill: currentColor;
  stroke-width: 0;
}

.filter-count {
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  top: calc(-1 * var(--nxt-space-xs) * 0.75);
  right: calc(-1 * var(--nxt-space-xs) * 0.75);
  min-width: calc(var(--nxt-size-unit) * 0.9);
  height: calc(var(--nxt-size-unit) * 0.9);
  padding: 0 calc(var(--nxt-space-2xs) * 0.75);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-surface-bg);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  font-size: calc(var(--nxt-font-size-xs) * 0.85);
  font-weight: var(--nxt-font-weight-bold);
  line-height: 1;
  text-align: center;
  box-sizing: border-box;
}

/* NxPopover caps its panel at 24rem incl. padding — everything in here must
   stay inside that budget, otherwise inputs paint past the panel edge. */
.filter-panel {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  max-width: 100%;
  font-weight: var(--nxt-font-weight-medium);
}

.filter-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-sm);
  padding-bottom: var(--nxt-space-2xs);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}
.filter-panel-title {
  min-width: 0;
  overflow: hidden;
  font-size: var(--nxt-font-size-sm);
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.filter-panel-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: calc(var(--nxt-size-unit) * 1.25);
  height: calc(var(--nxt-size-unit) * 1.25);
  padding-inline: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-bg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-bold);
}
.filter-panel.mode-list {
  min-width: var(--nxt-panel-width-md);
}
.filter-panel.mode-condition {
  width: min(var(--nxt-panel-width-xl), 80vw);
}

.check-list {
  display: flex;
  flex-direction: column;
  max-height: calc(var(--nxt-size-unit) * 14);
  overflow: auto;
}

.check-row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-sm);
  cursor: pointer;
  font-size: var(--nxt-font-size-sm);

  &:hover {
    background-color: var(--nxt-brand-subtle-bg);
  }
}

.check-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.check-note {
  padding: var(--nxt-space-2xs);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
}

.exclude-row {
  margin-top: var(--nxt-space-2xs);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: 0;
  padding-top: var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
}

.simple-footer {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  margin-top: var(--nxt-space-2xs);
}

.footer-spacer {
  flex: 1 1 auto;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nxt-space-xs);
}

.panel-title {
  font-weight: var(--nxt-font-weight-semibold);
  font-size: var(--nxt-font-size-sm);
}

.condition-row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}

.remove-condition {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: var(--nxt-control-size-xs);
  height: var(--nxt-control-size-xs);
  padding: 0;
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-muted);
  cursor: pointer;

  &:hover {
    background-color: var(--nxt-brand-subtle-bg);
    color: var(--nxt-fg);
  }
}

.add-condition {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  align-self: flex-start;
  padding: var(--nxt-space-2xs) var(--nxt-space-2xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-brand-bg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  cursor: pointer;

  &:hover {
    background-color: var(--nxt-brand-subtle-bg);
  }
}

.text-action {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-2xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-brand-bg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  cursor: pointer;

  &:hover {
    background-color: var(--nxt-brand-subtle-bg);
  }

  &.muted {
    color: var(--nxt-fg-muted);
  }
}

.panel-actions {
  display: flex;
  gap: var(--nxt-space-xs);
  justify-content: flex-end;
  margin-top: var(--nxt-space-2xs);
}
</style>
